import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

import { discussionCsApi, DiscussionCsApiError } from './chapterDiscussions.api'
import { DiscussionActionDialog } from './DiscussionActionDialog'

import type { DiscussionUserBulkDeleteJob } from './chapterDiscussions.types'

export interface DiscussionBulkDeleteJobPanelProps {
  jobId: string
}

export function DiscussionBulkDeleteJobPanel({
  jobId,
}: DiscussionBulkDeleteJobPanelProps) {
  const [job, setJob] = useState<DiscussionUserBulkDeleteJob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [retryOpen, setRetryOpen] = useState(false)
  const [pending, setPending] = useState(false)

  const load = useCallback(async () => {
    try {
      const response = await discussionCsApi.getBulkDeleteJob(jobId)
      setJob(response.job)
      setError(null)
      return response.job
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to load bulk-delete job',
      )
      return null
    }
  }, [jobId])

  useEffect(() => {
    let active = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async () => {
      const current = await load()
      if (
        active &&
        current &&
        (current.state === 'queued' || current.state === 'running')
      ) {
        timer = setTimeout(poll, 5000)
      }
    }
    poll()
    return () => {
      active = false
      if (timer) clearTimeout(timer)
    }
  }, [load])

  const retry = async (reason: string) => {
    setPending(true)
    try {
      const response = await discussionCsApi.retryBulkDeleteJob(jobId, {
        reason,
        expectedState: 'failed',
      })
      setJob(response.job)
      setRetryOpen(false)
      setError(null)
      window.dispatchEvent(new Event('discussion-detail-refresh'))
    } catch (requestError) {
      const apiError = requestError as DiscussionCsApiError
      setError(apiError.message)
      if (apiError.status === 409) {
        setRetryOpen(false)
        await load()
      }
    } finally {
      setPending(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">Delete-all job {jobId}</Typography>
        {job && <Chip size="small" label={job.state} />}
      </Stack>
      {!job && !error && <LinearProgress sx={{ mt: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {job && (
        <Box sx={{ mt: 2 }}>
          {(job.state === 'queued' || job.state === 'running') && (
            <LinearProgress sx={{ mb: 2 }} />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Typography variant="body2">Scanned: {job.scannedCount}</Typography>
            <Typography variant="body2">Deleted: {job.deletedCount}</Typography>
            <Typography variant="body2">Failed: {job.failedCount}</Typography>
            {job.remainingCount !== undefined && (
              <Typography variant="body2">
                Remaining: {job.remainingCount}
              </Typography>
            )}
          </Stack>
          {job.lastErrorCode && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Last error: {job.lastErrorCode}
            </Alert>
          )}
          {job.state === 'failed' && (
            <Button
              variant="outlined"
              color="warning"
              sx={{ mt: 2 }}
              onClick={() => setRetryOpen(true)}>
              Retry failed job
            </Button>
          )}
        </Box>
      )}
      <DiscussionActionDialog
        open={retryOpen}
        title="Retry delete-all job"
        description="Retry the current failed generation. Completed deletions will not be repeated."
        confirmLabel="Retry job"
        pending={pending}
        error={error}
        onClose={() => setRetryOpen(false)}
        onConfirm={retry}
      />
    </Paper>
  )
}
